/**
 * The nursing assessments tab contains several child components, each which handle the form group
 * for their respective assessment category. The assessment tab and the child components
 * communicate via two-way data binding: the assessment tab contains the Assessment object,
 * which is passed to and altered by the child (or form handler) components. This enables the
 * inputs to the forms to immediately update the main Assessment object.
 */

import { ChangeDetectorRef, Component, effect, inject, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NURSING_ASSESSMENTS_FIELD } from 'src/app/global';
import { PatientDashboardTabComponent } from 'src/app/patients/dashboard-page/dashboard-tab/dashboard-tab.component';
import { formToAssessment } from './assessment-converter.util';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { GeneralAppearanceFormComponent } from './general-appearance-form/general-appearance-form.component';
import { HeentFormComponent } from './heent-form/heent-form.component';
import { CardiovascularFormComponent } from './cardiovascular-form/cardiovascular-form.component';
import { RespiratoryFormComponent } from './respiratory-form/respiratory-form.component';
import { NeurologicalFormComponent } from './neurological-form/neurological-form.component';
import { MusculoskeletalFormComponent } from './musculoskeletal-form/musculoskeletal-form.component';
import { GastrointestinalFormComponent } from './gastrointestinal-form/gastrointestinal-form.component';
import { GenitourinaryFormComponent } from './genitourinary-form/genitourinary-form.component';
import { PsychosocialFormComponent } from './psychosocial-form/psychosocial-form.component';
import { IntegumentaryFormComponent } from './integumentary-form/integumentary-form.component';
import { ProviderNotifiedFormComponent } from './provider-notified-form/provider-notified-form.component';
import { PainAssessmentFormComponent } from './pain-assessment-form/pain-assessment-form.component';
import { initialFormState } from './assessments-tab.metadata';
import { AccordionModule } from 'primeng/accordion';
import { IntakeOutputFormComponent } from './intake-output-form/intake-output-form.component';
import { ButtonDirective } from 'primeng/button';
import { ExpansionPanelComponent } from '../../../shared/expansion-panel/expansion-panel.component';
import {
  ProviderNotifiedAssessmentHelpers,
  ProviderNotifiedFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/provider-notified.model';
import {
  PsychosocialAssessmentHelpers,
  PsychosocialFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/psychosocial.model';
import {
  IntakeOutputAssessmentHelpers,
  IntakeOutputFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/intake-output.model';
import {
  PainAssessmentHelpers,
  PainFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/pain.model';
import {
  IntegumentaryAssessmentHelpers,
  IntegumentaryFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/integumentary.model';
import {
  GenitourinaryAssessmentHelpers,
  GenitourinaryFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/genitourinary.model';
import {
  GastrointestinalAssessmentHelpers,
  GastrointestinalFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/gastrointestinal.model';
import {
  MusculoskeletalAssessmentHelpers,
  MusculoskeletalFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/musculoskeletal.model';
import {
  NeurologicalAssessmentHelpers,
  NeurologicalFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/neurological.model';
import {
  RespiratoryAssessmentHelpers,
  RespiratoryFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/respiratory.model';
import {
  CardiovascularAssessmentHelpers,
  CardiovascularFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/cardiovascular.model';
import {
  HEENTAssessmentHelpers,
  HEENTFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/HEENT.model';
import {
  GeneralAppearanceAssessmentHelpers,
  GeneralAppearanceFormGroup,
} from 'src/app/calvin-emr-shared/models/assessments/general-appearance.model';
import { MessageService } from 'primeng/api';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';
import { error } from 'console';

export interface FormControlData {
  control: UntypedFormControl;
  default: string;
  wnl: any;
}

@Component({
  selector: 'app-assessments-tab',
  templateUrl: './assessments-tab.component.html',
  styleUrls: ['./assessments-tab.component.scss'],
  standalone: true,
  imports: [
    AccordionModule,
    GeneralAppearanceFormComponent,
    HeentFormComponent,
    CardiovascularFormComponent,
    FormsModule,
    ReactiveFormsModule,
    RespiratoryFormComponent,
    NeurologicalFormComponent,
    MusculoskeletalFormComponent,
    GastrointestinalFormComponent,
    GenitourinaryFormComponent,
    PsychosocialFormComponent,
    IntegumentaryFormComponent,
    PainAssessmentFormComponent,
    IntakeOutputFormComponent,
    ButtonDirective,
    ExpansionPanelComponent,
    ProviderNotifiedFormComponent,
  ],
})
export class AssessmentsTabComponent extends PatientDashboardTabComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private ref = inject(ChangeDetectorRef);
  private toastService = inject(ToastService);

  assessments: any[] = [];

  patient!: Patient;

  /**
   * Overarching nursing assessment form
   *
   * Notes:
   *  o emr-checkbox-group elements should not include a "WNL" option; the "WNL" option will be
   *    implemented on its own in the component.
   */
  form = this.fb.group({
    // Date
    date: [''],

    // General appearance
    generalAppearance: GeneralAppearanceAssessmentHelpers.createFormGroup(),
    heent: HEENTAssessmentHelpers.createFormGroup(),
    cardiovascular: CardiovascularAssessmentHelpers.createFormGroup(),
    respiratory: RespiratoryAssessmentHelpers.createFormGroup(),
    neurological: NeurologicalAssessmentHelpers.createFormGroup(),
    musculoskeletal: MusculoskeletalAssessmentHelpers.createFormGroup(),
    gastrointestinal: GastrointestinalAssessmentHelpers.createFormGroup(),
    genitourinary: GenitourinaryAssessmentHelpers.createFormGroup(),
    psychosocial: PsychosocialAssessmentHelpers.createFormGroup(),
    integumentary: IntegumentaryAssessmentHelpers.createFormGroup(),
    painAssessment: PainAssessmentHelpers.createFormGroup(),
    intakeOutput: IntakeOutputAssessmentHelpers.createFormGroup(),
    providerNotified: ProviderNotifiedAssessmentHelpers.createFormGroup(),
  });

  // ViewChildren necessary to reset each form
  @ViewChild('generalApperanceForm') generalAppearanceForm!: GeneralAppearanceFormComponent;
  @ViewChild('heentForm') heentForm!: HeentFormComponent;
  @ViewChild('cardiovascularForm') cardiovascularForm!: CardiovascularFormComponent;
  @ViewChild('respiratoryForm') respiratoryForm!: RespiratoryFormComponent;
  @ViewChild('neurologicalForm') neurologicalForm!: NeurologicalFormComponent;
  @ViewChild('musculoskeletalForm') musculoskeletalForm!: MusculoskeletalFormComponent;
  @ViewChild('gastrointestinalForm') gastrointestinalForm!: GastrointestinalFormComponent;
  @ViewChild('genitourinaryForm') genitourinaryForm!: GenitourinaryFormComponent;
  @ViewChild('psychosocialForm') psychosocialForm!: PsychosocialFormComponent;
  @ViewChild('integumentaryForm') integumentaryForm!: IntegumentaryFormComponent;
  @ViewChild('painAssessmentForm') painAssessmentForm!: PainAssessmentFormComponent;
  @ViewChild('intakeOutputForm') intakeOutputForm!: IntakeOutputFormComponent;
  @ViewChild('providerNotifiedForm') providerNotifiedForm!: ProviderNotifiedFormComponent;

  constructor() {
    super();
    effect(() => {
      const patient = this.patientStore.patient();
      this.patient = patient;
      this.assessments = patient.assessments;
    });
  }

  ngOnInit(): void {}

  onSubmit(): void {
    // Enable the form to get values of previously-disabled controls
    this.form.enable();

    // Update assessments field in current patient
    const assessment = formToAssessment(this.form);
    this.assessments.push(assessment);
    this.assessments = ([] as any[]).concat(this.assessments);
    this.updatePatientField(NURSING_ASSESSMENTS_FIELD, this.assessments);

    // Reset
    this.resetAllForms();
    this.form.reset(initialFormState);

    // ('Assessment saved successfully.');
    // this.toastService.clearAndAddOne({
    //   severity: 'success',
    //   summary: 'Success',
    //   detail: 'Assessment saved successfully.',
    // });
  }

  /**
   * Sets all sub-forms back to initial state (namely the WNL values).
   */
  resetAllForms() {
    this.generalAppearanceForm?.reset();
    this.heentForm?.reset();
    this.cardiovascularForm?.reset();
    this.respiratoryForm?.reset();
    this.neurologicalForm?.reset();
    this.musculoskeletalForm?.reset();
    this.gastrointestinalForm?.reset();
    this.genitourinaryForm?.reset();
    this.psychosocialForm?.reset();
    this.integumentaryForm?.reset();

    // Eliminates the "Changed After Checked" error since we could be
    //  changing the WNL checkboxes from 'true' to 'false'
    this.ref.detectChanges();
  }

  get dateFormControl(): UntypedFormControl {
    return this.form.controls.date as UntypedFormControl;
  }

  get generalAppearanceFormGroup(): GeneralAppearanceFormGroup {
    return this.form.controls.generalAppearance as GeneralAppearanceFormGroup;
  }

  get heentFormGroup(): HEENTFormGroup {
    return this.form.controls.heent as HEENTFormGroup;
  }

  get cardiovascularFormGroup(): CardiovascularFormGroup {
    return this.form.controls.cardiovascular as CardiovascularFormGroup;
  }

  get respiratoryFormGroup(): RespiratoryFormGroup {
    return this.form.controls.respiratory as RespiratoryFormGroup;
  }

  get neurologicalFormGroup(): NeurologicalFormGroup {
    return this.form.controls.neurological as NeurologicalFormGroup;
  }

  get musculoskeletalFormGroup(): MusculoskeletalFormGroup {
    return this.form.controls.musculoskeletal as MusculoskeletalFormGroup;
  }

  get gastrointestinalFormGroup(): GastrointestinalFormGroup {
    return this.form.controls.gastrointestinal as GastrointestinalFormGroup;
  }

  get genitourinaryFormGroup(): GenitourinaryFormGroup {
    return this.form.controls.genitourinary as GenitourinaryFormGroup;
  }

  get psychosocialFormGroup(): PsychosocialFormGroup {
    return this.form.controls.psychosocial as PsychosocialFormGroup;
  }

  get integumentaryFormGroup(): IntegumentaryFormGroup {
    return this.form.controls.integumentary as IntegumentaryFormGroup;
  }

  get drainsAndTubesFormGroup(): UntypedFormGroup {
    return this.form.controls.drainsAndTubes as UntypedFormGroup;
  }

  get painFormGroup(): PainFormGroup {
    return this.form.controls.painAssessment as PainFormGroup;
  }

  get intakeOutputFormGroup(): IntakeOutputFormGroup {
    return this.form.controls.intakeOutput as IntakeOutputFormGroup;
  }

  get providerNotifiedFormGroup(): ProviderNotifiedFormGroup {
    return this.form.controls.providerNotified as ProviderNotifiedFormGroup;
  }
}
